import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  forwardRef,
  OnDestroy,
  OnInit,
  ViewChild,
  inject
} from '@angular/core';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { fromEvent, Subject, takeUntil, throttleTime } from 'rxjs';
import { HttpBaseService } from 'src/app/base/http-base.service';
import { UtilService } from 'src/app/services/util.service';
import { TranslateService } from '@ngx-translate/core';
import { ApiConstants } from 'src/app/base/api-constants';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
declare let bootstrap: any;

@Component({
  selector: 'vb-delta-sql-generation',
  templateUrl: './delta-sql-generation.component.html',
  styleUrls: ['./delta-sql-generation.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DeltaSqlGenerationComponent),
      multi: true
    }
  ],
  standalone: false  //NOSONAR
})
export class DeltaSqlGenerationComponent implements AfterViewInit, OnDestroy {
  private modalRef = inject(DynamicDialogRef);
  private util = inject(UtilService);
  private bs = inject(HttpBaseService);
  private translateService = inject(TranslateService);

  public schemaOptions: any[] = [
    { name: 'MySQL', code: 'mysql' },
    { name: 'PostgreSQL', code: 'postgres' },
    { name: 'Google BigQuery', code: 'bigquery' }
  ];
  public schemaType = this.schemaOptions[0];
  public originalSchema: string;
  public changedSchema: string;
  public deltaSQLSchema: string;
  public isLoadingResponse = false;
  public isDisabled = true;

  private destroy$ = new Subject<void>();

  editorInstances: Record<number, any> = {};
  resizeObservers: ResizeObserver[] = [];
  editorOptions = {
    language: 'sql',
    minimap: {
      enabled: false
    },
    renderLineHighlight: 'none',
    renderLineHighlightOnlyWhenFocus: false,
    wordWrap: 'on'
  };

  @ViewChild('monacoWrapper1') monacoWrapper1Ref!: ElementRef;
  @ViewChild('monacoWrapper2') monacoWrapper2Ref!: ElementRef;
  @ViewChild('monacoWrapper3') monacoWrapper3Ref!: ElementRef;
  @ViewChild('generateButton') generateBtn!: ElementRef;
  @ViewChild('downloadButton') downloadBtn!: ElementRef;
  @ViewChild('copyToast') toastElRef!: ElementRef;

  ngAfterViewInit(): void {
    this.throttleButtonClicks();
  }

  throttleButtonClicks() {
    fromEvent(this.generateBtn.nativeElement, 'click')
      .pipe(throttleTime(3000))
      .subscribe(() => {
        this.generateDeltaSchema();
      });

    fromEvent(this.downloadBtn.nativeElement, 'click')
      .pipe(throttleTime(3000))
      .subscribe(() => this.downloadSQL());
  }

  onEditorInit(editor: any, index: number) {
    this.editorInstances[index] = editor;
    this.observeResize(index);
  }

  observeResize(index: number) {
    const wrapperRef = this[`monacoWrapper${index}Ref`].nativeElement;

    const observer = new ResizeObserver(() => {
      this.editorInstances[index]?.layout();
    });

    observer.observe(wrapperRef);
    this.resizeObservers.push(observer);
  }

  triggerResize(index: number) {
    if (this.editorInstances[index]) {
      this.editorInstances[index].layout();
    }
  }

  generateDeltaSchema() {
    this.isLoadingResponse = true;
    this.updateButtonState();

    const requestBody = {
      schemaType: this.schemaType?.code,
      originalSchema: this.originalSchema,
      changedSchema: this.changedSchema
    };

    this.bs
      .post(ApiConstants.generateDeltaSQL, requestBody)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          console.log('API Response:', response);
          this.deltaSQLSchema = response?.body ? JSON.parse(response.body)?.data : '';
          this.isLoadingResponse = false;
          this.updateButtonState();
        },
        error: (error) => {
          console.error('API Error:', error);
          const errorMessage = this.translateService.instant(error?.error?.MESSAGE ?? 'DELTA_SQL_GENERATION_FAILED');
          this.util.showError(errorMessage);
          this.deltaSQLSchema = '';
          this.isLoadingResponse = false;
          this.updateButtonState();
        }
      });
  }

  copyToClipboard() {
    navigator.clipboard
      .writeText(this.deltaSQLSchema)
      .then((_val: any) => {
        const toastElement = this.toastElRef.nativeElement;
        const toast = new bootstrap.Toast(toastElement, { delay: 2000 });
        toast.show();
      })
      .catch((err) => {
        console.error('Failed to copy:', err);
      });
  }

  downloadSQL() {
    if (!this.deltaSQLSchema?.trim()) {
      this.util.showMessage('warn', this.translateService.instant('DELTA_SQL_EMPTY'));

      return;
    }

    const blob = new Blob([this.deltaSQLSchema], { type: 'text/sql' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    const randomFileName = `deltaSQL_${Date.now()}.sql`;

    a.href = url;
    a.download = randomFileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }

  onInputChange() {
    this.updateButtonState();
  }

  updateButtonState() {
    this.isDisabled = this.isLoadingResponse || !(this.originalSchema.trim() && this.changedSchema.trim());
  }

  closePopup() {
    this.modalRef.close(true);
    this.modalRef.destroy();
  }

  ngOnDestroy(): void {
    this.resizeObservers.forEach((observer) => observer.disconnect());
    this.resizeObservers = [];

    this.destroy$.next();
    this.destroy$.complete();
  }
}
